import api from "./client";


// ---------- Reposições: Diagnóstico de pedidos aguardando estoque ----------
export async function diagnosticoPedidosAguardandoEstoque(): Promise<
	Array<{
		pedido_id: number;
		num_pedido: string;
		motivo: string;
		detalhes?: string;
		reposicoes?: Array<{
			id: number;
			status: "pendente" | "em_andamento" | "concluido" | "cancelado";
		}>;
	}>
> {
	try {
		const { data } = await api.get(
			`/reposicao/diagnostico/pedidos-aguardando-estoque`,
		);
		return data;
	} catch (error: any) {
		console.error(
			"Erro ao buscar diagnóstico de pedidos aguardando estoque:",
			error,
		);
		throw new Error(
			"Falha ao carregar o diagnóstico de pedidos aguardando estoque.",
		);
	}
}


export async function pedidos(dados: {
	busca?: string;
	filtroStatus?: string;
	filtroCanal?: string;
	filtroMarcador?: string;
	filtroStatusEstoque?: string;
	data_inicio?: string;
	data_fim?: string;
	filtroNotaFiscal?: "com" | "sem";
	/** #1441: "hoje" = pedidos do ML com limite de despacho vencendo hoje. */
	filtroDespacho?: "hoje";
	sortField?: string;
	sortDirection?: string;
	offset?: number;
	limit?: number;
	includeContadores?: boolean;
}): Promise<any> {
	try {
		// Mapear parâmetros para o formato esperado pelo backend
		const params: any = {};
		if (dados.busca) params.search = dados.busca;
		if (dados.filtroStatus) params.filtroStatus = dados.filtroStatus;
		if (dados.filtroCanal) params.canal_venda = dados.filtroCanal;
		if (dados.filtroMarcador) params.filtroMarcador = dados.filtroMarcador;
		if (dados.filtroStatusEstoque)
			params.filtroStatusEstoque = dados.filtroStatusEstoque;
		if (dados.data_inicio) params.data_inicio = dados.data_inicio;
		if (dados.data_fim) params.data_fim = dados.data_fim;
		if (dados.filtroNotaFiscal)
			params.filtroNotaFiscal = dados.filtroNotaFiscal;
		// #1441: o recorte de "hoje" é resolvido no backend, no fuso da
		// operação — mandar um range calculado aqui erraria a virada do dia
		// para quem estivesse com o navegador em outro fuso.
		if (dados.filtroDespacho) params.filtroDespacho = dados.filtroDespacho;
		if (dados.sortField) params.sortField = dados.sortField;
		if (dados.sortDirection) params.sortDirection = dados.sortDirection;
		if (dados.offset !== undefined) params.offset = dados.offset.toString();
		if (dados.limit !== undefined) params.limit = dados.limit.toString();
		if (dados.includeContadores) params.includeContadores = "true";

		const response = await api.get("/pedido", { params });
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar pedidos:", error.message);
		throw new Error("Falha ao buscar pedidos.");
	}
}


export async function buscarProdutosDoPedido(pedido_id: number): Promise<any> {
	try {
		// ✅ Preferir novo modelo: 1 linha por reserva/localização (split)
		try {
			const resReservas = await api.get(
				`/produto-pedido/pedido/${pedido_id}/reservas`,
			);
			const data = resReservas.data;
			if (Array.isArray(data) && data.length > 0) {
				return data;
			}
		} catch {
			// fallback abaixo
		}

		// Fallback legado: 1 linha por produto_pedido
		const response = await api.get(`/produto-pedido/pedido/${pedido_id}`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar produtos do pedido:", error.message);
		throw new Error("Falha ao buscar produtos do pedido.");
	}
}


export async function buscarReservasDoPedido(pedido_id: number): Promise<any> {
	try {
		const response = await api.get(
			`/produto-pedido/pedido/${pedido_id}/reservas`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar reservas do pedido:", error.message);
		throw new Error("Falha ao buscar reservas do pedido.");
	}
}


export interface TrocaPedido {
	troca_id: number;
	status: string;
	erro_inicial?: string;
	// Campos adicionais para identificar o motivo/causa da ocorrência
	tipo_erro?: string;
	motivo?: string;
	retorno_tiny?: {
		id?: number;
		descricao?: string;
		sku?: string;
		preco?: string;
		ean?: string;
		gtin?: string;
		quantidade?: number;
	};
	produto_troca?: {
		produto?: { sku?: string; descricao?: string; ean?: string };
	};
	produto_original?: {
		produto?: { sku?: string; descricao?: string; ean?: string };
	};
}


export async function buscarTrocasPorPedido(
	pedido_id: number,
): Promise<TrocaPedido[]> {
	try {
		const res = await api.get(`/troca/pedido/${pedido_id}`);
		return Array.isArray(res.data) ? res.data : [];
	} catch (error: any) {
		console.error("Erro ao buscar trocas do pedido:", error?.message || error);
		return [];
	}
}


/**
 * Dispara a sincronização de pedidos no backend (Tiny → WMS).
 */
export async function sincronizarPedidos(): Promise<{
	sucesso: boolean;
	message: string;
	erroTiny?: string | null;
	erroOnsite?: string | null;
}> {
	try {
		const res = await api.post("/pedido/sincronizar-pedidos");
		return res.data;
	} catch (error: any) {
		console.error("Erro ao sincronizar pedidos:", error?.message);
		const msg =
			error?.response?.data?.message || "Falha ao sincronizar pedidos.";
		throw new Error(msg);
	}
}


/**
 * Dispara a sincronização de pedidos Onsite no backend (Tiny → WMS).
 * Apenas para debug/teste.
 */
export async function sincronizarPedidosOnsite(): Promise<{
	message: string;
	pedidosProcessados?: number;
	pedidosIgnorados?: number;
}> {
	try {
		const res = await api.post("/pedido/sincronizar-pedidos-onsite");
		return res.data;
	} catch (error: any) {
		console.error("Erro ao sincronizar pedidos Onsite:", error?.message);
		const msg =
			error?.response?.data?.message || "Falha ao sincronizar pedidos Onsite.";
		throw new Error(msg);
	}
}


/**
 * A troca de produto que o sync trouxe da Tiny para um pedido em divergência
 * na embalagem (#1931). `aplicada: false` = a troca foi vista, mas o pedido
 * ficou como estava; `mensagem` diz o que mudou, ou por que não mudou.
 */
export interface TrocaDeProdutoSincronizada {
	aplicada: boolean;
	num_pedido: string;
	mensagem: string;
	unidades_que_sairam: number;
	unidades_que_entraram: number;
	contagem_zerada: boolean;
	nota_solta: boolean;
	voltou_para_fila: boolean;
	unidades_em_separacao: number;
	unidades_em_reposicao: number;
	unidades_com_o_lider: number;
}

/**
 * Sincroniza um pedido específico:
 * - Remove reservas dos itens
 * - Rebusca os produtos no ERP e recria os itens do pedido
 *
 * Em pedido com divergência na embalagem, só traz o cancelamento e a troca de
 * produto feita na Tiny — que volta em `troca_de_produto`.
 */
export async function sincronizarPedido(
	pedido_id: number,
): Promise<{ message: string; troca_de_produto?: TrocaDeProdutoSincronizada }> {
	try {
		const res = await api.post(`/pedido/${pedido_id}/sincronizar`);
		return res.data;
	} catch (error: any) {
		const msg =
			error?.response?.data?.message || "Falha ao sincronizar o pedido.";
		throw new Error(msg);
	}
}


/**
 * Sincroniza múltiplos pedidos específicos em lote (Tiny → WMS).
 * Cada pedido é sincronizado individualmente no backend.
 */
export async function sincronizarPedidosLote(
	pedido_ids: number[],
): Promise<{
	sucesso: number[];
	erros: Array<{ pedido_id: number; mensagem: string }>;
}> {
	try {
		const res = await api.post("/pedido/sincronizar-lote", { pedido_ids });
		return res.data;
	} catch (error: any) {
		const msg =
			error?.response?.data?.message || "Falha ao sincronizar pedidos em lote.";
		throw new Error(msg);
	}
}


export async function buscarResumoCanais(
	canais: Array<{ nome: string; data_inicio: string; data_fim: string }>,
): Promise<{
	todos: Record<number, number>;
	por_canal: Record<string, Record<number, number>>;
}> {
	try {
		const response = await api.post("/pedido/contadores/resumo-canais", {
			canais,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar resumo de canais:", error.message);
		throw new Error("Falha ao buscar resumo de canais.");
	}
}


export async function buscarContadoresStatus(): Promise<
	Record<string, { label: string; color: string; count: number }>
> {
	try {
		const response = await api.get("/pedido/contadores/status");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar contadores de status:", error.message);
		throw new Error("Falha ao buscar contadores de status.");
	}
}


export async function buscarContadoresStatusFiltrados(params?: {
	search?: string;
	canal_venda?: string;
	filtroMarcador?: string;
	data_inicio?: string;
	data_fim?: string;
	filtroStatusEstoque?: string;
	filtroNotaFiscal?: "com" | "sem";
	/** #1441: mantém os contadores no mesmo recorte da tabela. */
	filtroDespacho?: "hoje";
}): Promise<Record<string, { label: string; color: string; count: number }>> {
	try {
		const queryParams: Record<string, any> = {};

		if (params?.search) queryParams.search = params.search;
		if (params?.canal_venda) queryParams.canal_venda = params.canal_venda;
		if (params?.filtroMarcador)
			queryParams.filtroMarcador = params.filtroMarcador;
		if (params?.data_inicio) queryParams.data_inicio = params.data_inicio;
		if (params?.data_fim) queryParams.data_fim = params.data_fim;
		if (params?.filtroStatusEstoque)
			queryParams.filtroStatusEstoque = params.filtroStatusEstoque;
		if (params?.filtroNotaFiscal)
			queryParams.filtroNotaFiscal = params.filtroNotaFiscal;
		if (params?.filtroDespacho)
			queryParams.filtroDespacho = params.filtroDespacho;

		const response = await api.get("/pedido/contadores/status-filtrados", {
			params: queryParams,
		});
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao buscar contadores de status filtrados:",
			error.message,
		);
		throw new Error("Falha ao buscar contadores de status filtrados.");
	}
}


// ============================================================================
// FILA DE SINCRONIZAÇÃO DE PEDIDOS
// ============================================================================

export type SyncQueueStatus =
	| "PENDENTE"
	| "PROCESSANDO"
	| "IMPORTADO"
	| "ESTOQUE_OK"
	| "CONCLUIDO"
	| "FALHA"
	| "FALHA_PERMANENTE"
	| "IGNORADO";

export type SyncQueuePhase =
	| "IMPORT"
	| "STOCK"
	| "TASK"
	| "INVOICE"
	| "LABEL";

export interface SyncQueueItem {
	id: number;
	tiny_id: number;
	separacao_id: number | null;
	status: SyncQueueStatus;
	phase: SyncQueuePhase;
	tentativas: number;
	max_tentativas: number;
	ultimo_erro: string | null;
	pedido_id: number | null;
	pedido: { num_pedido: string } | null;
	criado_em: string;
	atualizado_em: string;
	proxima_tentativa_em: string | null;
	concluido_em: string | null;
}

/** Contagem por status dentro de uma fase da fila (#1739). */
export interface SyncQueueFaseStats {
	pendentes: number;
	processando: number;
	importados: number;
	estoque_ok: number;
	concluidos: number;
	falhas: number;
	falhasPermanentes: number;
	ignorados: number;
	total: number;
}

/**
 * O que a tela consome — tudo preenchido.
 *
 * Quem monta isso é `normalizarStats` no hook, a partir da resposta crua. Os
 * componentes podem ler `porFase.importacao` direto, sem defesa.
 */
export interface SyncQueueStats extends SyncQueueFaseStats {
	/** Linhas com `importado_em` preenchido — o pedido está no WMS (#1739). */
	importados_total: number;
	porFase: {
		/** IMPORT + STOCK + TASK. */
		importacao: SyncQueueFaseStats;
		/** INVOICE — emissão da NF depois da separação. */
		faturamento: SyncQueueFaseStats;
		/** LABEL — busca da etiqueta de envio depois da nota. */
		etiqueta: SyncQueueFaseStats;
	};
}

/**
 * O que o endpoint devolve de fato.
 *
 * `porFase` e `importados_total` chegaram na #1739: backend anterior não manda
 * nenhum dos dois. São opcionais aqui para que a normalização no hook seja
 * exigida pelo compilador, em vez de parecer defesa sobrando.
 */
export type SyncQueueStatsResposta = SyncQueueFaseStats &
	Partial<Pick<SyncQueueStats, "importados_total" | "porFase">>;

export interface SyncQueueListResponse {
	items: SyncQueueItem[];
	total: number;
}

export async function buscarSyncQueueEstatisticas(params?: {
	dataInicio?: string;
}): Promise<SyncQueueStatsResposta> {
	const response = await api.get("/pedido/sync-queue/estatisticas", { params });
	return response.data;
}

export async function listarSyncQueue(params?: {
	status?: SyncQueueStatus;
	phase?: SyncQueuePhase;
	tinyId?: number;
	dataInicio?: string; // ISO date (YYYY-MM-DD ou completo)
	dataFim?: string;
	offset?: number;
	limit?: number;
}): Promise<SyncQueueListResponse> {
	const response = await api.get("/pedido/sync-queue", { params });
	return response.data;
}

export async function buscarSyncQueuePorTinyId(
	tinyId: number,
): Promise<SyncQueueItem> {
	const response = await api.get(`/pedido/sync-queue/${tinyId}`);
	return response.data;
}

export async function reprocessarSyncQueueItem(
	id: number,
): Promise<{ message: string }> {
	const response = await api.post(`/pedido/sync-queue/${id}/reprocessar`);
	return response.data;
}

export async function reprocessarSyncQueueLote(
	ids: number[],
): Promise<{ message: string; affected: number }> {
	const response = await api.post(`/pedido/sync-queue/reprocessar-lote`, {
		ids,
	});
	return response.data;
}

/** Dispara um ciclo da fila na mão, sem esperar o job (#1746). */
export async function processarFilaAgora(): Promise<{ message: string }> {
	const response = await api.post(`/pedido/sync-queue/processar`);
	return response.data;
}

/** Reenfileira uma linha qualquer, não só DLQ (#1746). */
export async function reenfileirarSyncQueueItem(
	id: number,
): Promise<{ message: string }> {
	const response = await api.post(`/pedido/sync-queue/${id}/reenfileirar`);
	return response.data;
}

/** Traz um pedido do Tiny para a fila sem rodar o sync inteiro (#1746). */
export async function enfileirarSyncQueuePorTinyId(
	tinyId: number,
): Promise<{ message: string; item: SyncQueueItem | null }> {
	const response = await api.post(`/pedido/sync-queue/enfileirar`, { tinyId });
	return response.data;
}

export async function cancelarProcessamentoFila(): Promise<{ message: string }> {
	const response = await api.post(`/pedido/sync-queue/cancelar`);
	return response.data;
}

export async function cancelarSincronizacao(): Promise<{ message: string }> {
	const response = await api.post(`/pedido/sincronizar-pedidos/cancelar`);
	return response.data;
}

export interface SyncStatusResponse {
	syncRunning: boolean;
	queueProcessing: boolean;
}

export async function getSyncStatus(): Promise<SyncStatusResponse> {
	const response = await api.get(`/pedido/sync/status`);
	return response.data;
}

/**
 * Estado do faturamento do pedido no WMS (#1425).
 * `null`/ausente = nunca faturou por aqui.
 */
export type FaturamentoStatus =
	| "EM_ANDAMENTO"
	| "EMITIDA"
	| "FALHA"
	| "INDETERMINADO"
	| "BLOQUEADO";

export interface GerarNotaFiscalResponse {
	sucesso: boolean;
	pedido_id: number;
	numero_nota_fiscal?: string;
	mensagem: string;
}

/**
 * Emite a nota fiscal do pedido na Tiny e vincula a nota ao pedido.
 *
 * O backend recusa pedido não separado e trava emissão concorrente, então
 * clique repetido não gera segunda nota — mas devolve `sucesso: false` com a
 * mensagem do motivo. Quem chama decide o que mostrar.
 */
export async function gerarNotaFiscalPedido(
	pedido_id: number,
): Promise<GerarNotaFiscalResponse> {
	try {
		const res = await api.post(`/pedido/${pedido_id}/gerar-nota-fiscal`);
		return res.data;
	} catch (error: any) {
		const msg =
			error?.response?.data?.message || "Falha ao gerar a nota fiscal.";
		throw new Error(msg);
	}
}

// ==================== FILA DO MERCADO LIVRE (#1703) ====================
// Fila própria, ao lado da do Tiny: aqui o pedido do marketplace ainda não tem
// `tiny_id`, então não cabe na `pedido_sync_queue`.

export type MlPendenteStatus =
	| "PENDENTE"
	| "RESOLVIDO"
	| "DESCIDO"
	| "AMBIGUO"
	| "IGNORADO"
	| "JA_NO_WMS";

export interface MlPendenteItem {
	id: number;
	order_id: string;
	pack_id: string | null;
	seller_id: string;
	status: MlPendenteStatus;
	motivo_ignorado: string | null;
	logistic_type: string | null;
	shipment_id: string | null;
	data_pedido: string | null;
	tiny_id: number | null;
	pedido_id: number | null;
	tentativas: number;
	proxima_tentativa_em: string | null;
	ultimo_erro: string | null;
	coletado_em: string;
	atualizado_em: string;
}

export interface MlPendenteLista {
	itens: MlPendenteItem[];
	total: number;
}

export interface MlPendenteStats {
	porStatus: Partial<Record<MlPendenteStatus, number>>;
	/** Pendentes só esperando a próxima tentativa — backoff, não erro. */
	aguardandoTentativa: number;
	total: number;
}

export async function listarMlPendentes(params?: {
	status?: MlPendenteStatus;
	sellerId?: string;
	numero?: string;
	offset?: number;
	limit?: number;
}): Promise<MlPendenteLista> {
	const response = await api.get("/pedido/ml-pendente", { params });
	return response.data;
}

export async function buscarMlPendenteEstatisticas(): Promise<MlPendenteStats> {
	const response = await api.get("/pedido/ml-pendente/estatisticas");
	return response.data;
}

export async function tentarMlPendenteAgora(
	id: number,
): Promise<{ message: string }> {
	const response = await api.post(`/pedido/ml-pendente/${id}/tentar-agora`);
	return response.data;
}

export async function descartarMlPendente(
	id: number,
): Promise<{ message: string }> {
	const response = await api.post(`/pedido/ml-pendente/${id}/descartar`);
	return response.data;
}
